/**
 * §7 Advanced: wire an integration. Builds ONE overlay derived/side_effects
 * entry: capability from the deployment catalog (server re-checks with
 * overlay_unknown_capability), args mapped with $slots. autocomplete,
 * guided depends_on. "Replace" a shipped integration = disable it
 * (EntryControls) + add yours — the spec §5.1 pattern.
 */
import { useState } from 'react'
import { useCapabilities } from '@/hooks/useCapabilities'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import type { OrderWorkflowDefinitionJson } from '@/types/workflowDefinition'

const KEY_RE = /^[a-z][a-z0-9_]*$/
const inputCls =
  'rounded-sm border border-border bg-background p-1 text-xs text-foreground'

interface ArgRow { name: string; value: string }

export function AddAutomationForm({ base }: { base: OrderWorkflowDefinitionJson }) {
  const advanced = useWorkflowConfigStore((s) => s.advanced)
  const overlay = useWorkflowConfigStore((s) => s.overlay)
  const addAutomation = useWorkflowConfigStore((s) => s.addAutomation)
  const { data: capabilities, isLoading } = useCapabilities()

  const [open, setOpen] = useState(false)
  const [section, setSection] = useState<'derived' | 'side_effects'>('derived')
  const [name, setName] = useState('')
  const [capability, setCapability] = useState('')
  const [args, setArgs] = useState<ArgRow[]>([{ name: '', value: '' }])
  const [dependsOn, setDependsOn] = useState<string[]>([])
  const [writes, setWrites] = useState('')
  const [when, setWhen] = useState('')
  const [compensate, setCompensate] = useState('')

  if (!advanced) return null

  // Overlay-added, non-tombstone entries must be pickable too (§7 flagship
  // flow: add slot -> wire an integration that uses it). A tombstone is
  // exactly {disabled: true}; skip those AND base keys the overlay disables.
  const isTombstone = (v: unknown) =>
    !!v && typeof v === 'object' && Object.keys(v).length === 1 && (v as { disabled?: unknown }).disabled === true

  const mergedKeys = (
    baseSection: Record<string, unknown> | undefined,
    overlaySection: Record<string, unknown> | undefined,
  ): string[] => {
    const result = Object.keys(baseSection ?? {}).filter(
      (k) => !isTombstone(overlaySection?.[k]),
    )
    for (const k of Object.keys(overlaySection ?? {})) {
      if (!(k in (baseSection ?? {})) && !isTombstone(overlaySection![k]) && !result.includes(k)) {
        result.push(k)
      }
    }
    return result
  }

  const slotKeys = mergedKeys(base.slots, overlay.slots)
  const dependables = [
    ...slotKeys,
    ...mergedKeys(base.needs, overlay.needs),
    ...mergedKeys(base.derived, overlay.derived),
    ...mergedKeys(base.side_effects, overlay.side_effects),
  ]
  const taken = new Set([
    ...Object.keys(base.derived ?? {}),
    ...Object.keys(base.side_effects ?? {}),
    ...Object.keys(overlay.derived ?? {}),
    ...Object.keys(overlay.side_effects ?? {}),
  ])
  const nameTaken = taken.has(name)
  const nameInvalid = name !== '' && !KEY_RE.test(name)
  const canSubmit = name !== '' && !nameTaken && !nameInvalid && capability !== ''

  const reset = () => {
    setOpen(false); setSection('derived'); setName(''); setCapability('')
    setArgs([{ name: '', value: '' }]); setDependsOn([]); setWrites(''); setWhen('')
    setCompensate('')
  }

  const submit = () => {
    // Build by omission — undefined never enters the overlay (clone() drops it).
    const spec: Record<string, unknown> = { capability }
    const argEntries = args.filter((a) => a.name.trim() && a.value.trim())
    if (argEntries.length > 0) {
      spec.args = Object.fromEntries(argEntries.map((a) => [a.name.trim(), a.value.trim()]))
    }
    if (dependsOn.length > 0) spec.depends_on = dependsOn
    if (section === 'derived') {
      if (writes.trim()) spec.writes = writes.trim()
      if (when.trim()) spec.when = when.trim()
    } else if (compensate) {
      spec.compensate = compensate
    }
    addAutomation(section, name, spec)
    reset()
  }

  if (!open) {
    return (
      <button
        type="button"
        className="rounded-xl border border-dashed border-border text-muted-foreground px-3 py-1.5 text-xs font-semibold transition hover:border-primary hover:bg-primary/10 hover:text-primary"
        onClick={() => setOpen(true)}
      >
        + Add an automation
      </button>
    )
  }

  const byCategory = new Map<string, { id: string; description: string | null }[]>()
  for (const cap of capabilities ?? []) {
    const list = byCategory.get(cap.category) ?? []
    list.push({ id: cap.id, description: cap.description })
    byCategory.set(cap.category, list)
  }

  return (
    <div className="rounded-xl border border-border p-3 space-y-2 text-xs text-foreground">
      <p className="font-medium text-foreground">Add an automation</p>
      <datalist id="wf-slot-paths">
        {slotKeys.map((k) => (
          <option key={k} value={`$slots.${k}`} />
        ))}
      </datalist>

      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-1.5">
          Runs when
          <select aria-label="runs when" className={inputCls} value={section}
            onChange={(e) => setSection(e.target.value as typeof section)}>
            <option value="derived">its inputs are known (derived value)</option>
            <option value="side_effects">the order advances (side effect)</option>
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          Name
          <input aria-label="name" className={`${inputCls} w-36 font-mono`} value={name}
            onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex items-center gap-1.5">
          Capability
          <select aria-label="capability" className={inputCls} value={capability}
            onChange={(e) => setCapability(e.target.value)}>
            <option value="">{isLoading ? 'Loading…' : '— pick a capability —'}</option>
            {Array.from(byCategory.entries()).map(([category, caps]) => (
              <optgroup key={category} label={category}>
                {caps.map((c) => (
                  <option key={c.id} value={c.id} title={c.description ?? undefined}>
                    {c.id}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      </div>
      {nameTaken && <p className="text-destructive">That name already exists in this workflow.</p>}
      {nameInvalid && <p className="text-destructive">Names are lowercase letters, digits and underscores, starting with a letter.</p>}

      <div className="space-y-1">
        <p>Arguments (values can reference collected info — type $slots. to autocomplete)</p>
        {args.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <input aria-label={`argument ${i + 1} name`} className={`${inputCls} w-32 font-mono`}
              value={row.name} placeholder="name"
              onChange={(e) =>
                setArgs((a) => a.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))
              } />
            <input aria-label={`argument ${i + 1} value`} className={`${inputCls} w-56 font-mono`}
              value={row.value} placeholder="$slots.…" list="wf-slot-paths"
              onChange={(e) =>
                setArgs((a) => a.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))
              } />
          </div>
        ))}
        <button type="button" className="text-primary hover:underline"
          onClick={() => setArgs((a) => [...a, { name: '', value: '' }])}>
          + another argument
        </button>
      </div>

      <fieldset className="space-y-1">
        <legend>Wait for (depends on)</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {dependables.map((k) => (
            <label key={k} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                aria-label={k}
                checked={dependsOn.includes(k)}
                onChange={(e) =>
                  setDependsOn((d) =>
                    e.target.checked ? [...d, k] : d.filter((x) => x !== k),
                  )
                }
              />
              <span className="font-mono">{k}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {section === 'derived' ? (
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-1.5">
            Fills (writes)
            <input aria-label="fills" className={`${inputCls} w-32 font-mono`} value={writes}
              onChange={(e) => setWrites(e.target.value)} />
          </label>
          <label className="flex items-center gap-1.5">
            Only when (optional)
            <input aria-label="only when" className={`${inputCls} w-56 font-mono`} value={when}
              onChange={(e) => setWhen(e.target.value)}
              placeholder="payment_method == 'transfer'" />
          </label>
        </div>
      ) : (
        <label className="flex items-center gap-1.5">
          If it needs undoing, run (optional)
          <select aria-label="undo action" className={inputCls} value={compensate}
            onChange={(e) => setCompensate(e.target.value)}>
            <option value="">— none —</option>
            {(capabilities ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.id}</option>
            ))}
          </select>
        </label>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" className="rounded-sm px-3 py-1.5 font-medium text-foreground hover:bg-muted" onClick={reset}>
          Cancel
        </button>
        <button type="button" disabled={!canSubmit}
          className="rounded-sm bg-primary px-3 py-1.5 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          onClick={submit}>
          Add
        </button>
      </div>
    </div>
  )
}
